import type { Profile } from "@wakeloop/protocol";
import type { SpaceMembersSnapshot } from "@wakeloop/schemas/types";
import type { WakeLoopProfile } from "../config/wakeloop-profiles-api";
import type { TranscriptSessionProfileIdentity } from "./transcript/transcript-item-builder";
import type { SpaceSessionContext } from "./view/space-session-view-state";

export type SpaceSessionProfileIdentity = TranscriptSessionProfileIdentity & {
  wakeLoopProfileId: string;
  profileHandle?: string;
  profileName: string;
  agentControllerRef?: string;
  agentControllerConversationId?: string;
};

export function buildSpaceSessionProfileIdentity(input: {
  wakeLoopProfile: WakeLoopProfile;
  agentControllerConversationId?: string | null;
}): SpaceSessionProfileIdentity {
  const { wakeLoopProfile } = input;
  const agentControllerConversationId = String(
    input.agentControllerConversationId ?? ""
  ).trim();

  return {
    kind: wakeLoopProfile.profileKind === "agent" ? "agent" : "human",
    wakeLoopProfileId: wakeLoopProfile.wakeLoopProfileId,
    profileHandle: wakeLoopProfile.profileHandle,
    profileName: wakeLoopProfile.profileName,
    ...(wakeLoopProfile.ownerUserId
      ? { ownerUserId: String(wakeLoopProfile.ownerUserId).trim() }
      : {}),
    ...(wakeLoopProfile.ownerName
      ? { ownerName: String(wakeLoopProfile.ownerName).trim() }
      : {}),
    ...(agentControllerConversationId ? { agentControllerConversationId } : {}),
  };
}

export function buildSpaceSessionProfileContext(
  wakeLoopProfile: WakeLoopProfile
): SpaceSessionContext["profile"] {
  return {
    profileId: wakeLoopProfile.wakeLoopProfileId,
    profileName: wakeLoopProfile.profileName,
    kind: wakeLoopProfile.profileKind === "agent" ? "agent" : "human",
    ownerUserId: wakeLoopProfile.ownerUserId ?? null,
    ownerName: wakeLoopProfile.ownerName ?? null,
  };
}

export function buildSpaceSessionProfileContextFromIdentity(input: {
  kind: "human" | "agent";
  ownerName?: string | null;
  ownerUserId?: string | null;
  wakeLoopProfileId: string;
  profileName: string;
}): SpaceSessionContext["profile"] {
  return {
    profileId: input.wakeLoopProfileId,
    profileName: input.profileName,
    kind: input.kind,
    ownerUserId: input.ownerUserId ?? null,
    ownerName: input.ownerName ?? null,
  };
}

export function toProtocolProfile(
  identity: SpaceSessionProfileIdentity
): Profile {
  return {
    kind: identity.kind,
    profileId: identity.wakeLoopProfileId,
    ...(identity.profileHandle
      ? { profileHandle: identity.profileHandle }
      : {}),
    profileName: identity.profileName,
    ...(identity.ownerUserId ? { owner: identity.ownerUserId } : {}),
    ...(identity.ownerName ? { ownerName: identity.ownerName } : {}),
    ...(identity.agentControllerRef
      ? { agentControllerRef: identity.agentControllerRef }
      : {}),
    ...(identity.agentControllerConversationId
      ? {
          agentControllerConversationId: identity.agentControllerConversationId,
        }
      : {}),
  };
}

export function buildActiveSpaceSessionContext(input: {
  profile: SpaceSessionContext["profile"];
  baseUrl: string;
  connectionMode: SpaceSessionContext["connectionMode"];
  session: SpaceSessionContext["session"];
}): SpaceSessionContext {
  return {
    surface: "active-session",
    connectionMode: input.connectionMode,
    gatewayUrl: input.baseUrl,
    profile: input.profile,
    session: input.session,
  };
}

export function rememberSessionOwnerDisplayName(
  ownerDisplayNameByUserId: Map<string, string>,
  wakeLoopProfile: WakeLoopProfile
): void {
  rememberOwnerDisplayName(
    ownerDisplayNameByUserId,
    wakeLoopProfile.ownerUserId,
    wakeLoopProfile.ownerName
  );
  if (wakeLoopProfile.profileKind === "human") {
    rememberOwnerDisplayName(
      ownerDisplayNameByUserId,
      wakeLoopProfile.ownerUserId,
      wakeLoopProfile.profileName,
      { overwrite: false }
    );
  }
}

export function rememberOwnerDisplayName(
  ownerDisplayNameByUserId: Map<string, string>,
  ownerUserIdRaw: string | null | undefined,
  ownerDisplayRaw: string | null | undefined,
  options?: {
    overwrite?: boolean;
  }
): void {
  const ownerUserId = String(ownerUserIdRaw ?? "").trim();
  const ownerDisplay = String(ownerDisplayRaw ?? "").trim();
  if (!(ownerUserId && ownerDisplay)) {
    return;
  }
  if (
    options?.overwrite === false &&
    ownerDisplayNameByUserId.has(ownerUserId)
  ) {
    return;
  }
  ownerDisplayNameByUserId.set(ownerUserId, ownerDisplay);
}

export function rememberMembersSnapshotOwnerDisplayNames(
  ownerDisplayNameByUserId: Map<string, string>,
  membersSnapshot: SpaceMembersSnapshot | null | undefined
): void {
  for (const human of membersSnapshot?.humans ?? []) {
    rememberOwnerDisplayName(
      ownerDisplayNameByUserId,
      human.ownerUserId,
      human.ownerName ?? human.profileName
    );
  }
  for (const agent of membersSnapshot?.agents ?? []) {
    rememberOwnerDisplayName(
      ownerDisplayNameByUserId,
      agent.ownerUserId,
      agent.ownerName
    );
  }
}
